import React, { useState } from 'react';

import { AwardJapanStandard } from '@travel/icons/award';
import { Spot } from '@travel/icons/ui';
import JapanStandardPopup from '@travel/traveler-core/components/JapanStandardPopup';
import { useTranslation } from '@travel/traveler-core/hooks';
import { IconText, IconTextList, RakutenAward, Tag } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import NearbyTerminals from 'components/NearbyTerminals';

import { Translate } from 'core/translate';
import { mappedIconTextListData } from 'pages/ProviderInformationPage/helpers';
import { ProviderItem } from 'ProviderList-Types';

import styles from './providerBox.module.scss';

type Props = {
  /** Object of provider data */
  item: ProviderItem;
  /** Type to define the current screen type */
  deviceType: string;
};

function ProviderDetailsBox(props: Props) {
  const { item, deviceType } = props;

  const features = mappedIconTextListData(item.features, styles.featureIcon);

  const rakutenAward = isNotEmptyArray(item.certifications)
    ? item.certifications
        .filter(award => award.image)
        .map(award => (
          <RakutenAward
            key={award?.name}
            title={award?.name}
            image={award?.image}
            imageClassName={styles.awardImage}
            data-testid={`providerDetailBox-award-${award?.name}`}
          />
        ))[0]
    : null;

  const [showJapanStandardPopup, setShowJapanStandardPopup] = useState(false);
  const japanStandardPopupAriaLabel = useTranslation({ id: 'Traveler_Common.Japan_Quality.Name' });

  const handleBubbling = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  return (
    <div className={styles.itemAmenitiesAndInfo}>
      {showJapanStandardPopup && (
        <button onClick={handleBubbling} className={styles.japanStandardBtn}>
          <JapanStandardPopup
            isOpen={showJapanStandardPopup}
            onCloseCallback={() => setShowJapanStandardPopup(false)}
          />
        </button>
      )}
      <div className={styles.awardsContainer}>
        {rakutenAward}
        {item.isJapanStandard && (
          <button
            className={styles.japanStandardAward}
            data-testid="providerDetailBox-japanStandardAward"
            aria-label={japanStandardPopupAriaLabel}
            onClick={event => {
              handleBubbling(event);
              setShowJapanStandardPopup(true);
            }}
          >
            <AwardJapanStandard className={styles.award} />
          </button>
        )}
      </div>

      {item.freeUntil && (
        <Tag
          type="outlined"
          color="green"
          className={styles.providerTag}
          data-testid="providerDetailBox-freeCancelation-tag"
        >
          <Translate id="Cancellation_Policy_Common.Provider_List.Free_Cancellation" />
        </Tag>
      )}

      <div className={styles.featuresContainer}>
        {isNotEmptyArray(features) && (
          <IconTextList
            className={styles.featuresList}
            items={features.slice(0, 3)}
            iconTextSpacing={4}
            iconTextClassName={styles.textStyle}
          />
        )}
      </div>

      {deviceType !== 'sp' && item.nearbyTerminal && (
        <IconText
          className={cx(styles.textStyle, styles.iconTextStyle)}
          icon={<Spot size={16} />}
          text={<NearbyTerminals {...item.nearbyTerminal} />}
          spaceBetween={4}
        />
      )}
    </div>
  );
}

export default ProviderDetailsBox;
